/**
 * Hook personalizado para manejar autenticación
 * Sincroniza el estado de Firebase Auth con el store de Zustand
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/authStore';
import { onAuthStateChange, getIdToken, getUserRole, logout } from '@/shared/services/authService';

export function useAuth() {
  const { setAuth, clearAuth, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigated = useRef(false);

  useEffect(() => {
    hasNavigated.current = false; // Reset al cambiar de ruta
    
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Obtener token (forzar refresh para obtener custom claims actualizados)
          const token = await getIdToken(true);
          if (!token) {
            clearAuth();
            return;
          }

          // Obtener el rol del usuario desde los custom claims
          const userRole = await getUserRole();

          // Verificar que el usuario sea admin
          if (userRole !== 'admin') {
            // Cerrar sesión si no es admin
            await logout();
            clearAuth();
            // Redirigir a login si no estamos en una ruta pública y no hemos navegado ya
            const publicPaths = ['/login', '/register'];
            const isPublic =
            publicPaths.includes(location.pathname) ||
            location.pathname.startsWith('/privacy/') ||
            location.pathname === '/delete-data' ||
            location.pathname === '/delete-account';
            if (!isPublic && !hasNavigated.current) {
              hasNavigated.current = true;
              navigate('/login', { replace: true });
            }
            return;
          }

          // Si es admin, establecer autenticación
          setAuth(firebaseUser, 'admin');
        } catch (error) {
          console.error('Error al obtener token o rol:', error);
          clearAuth();
        }
      } else {
        clearAuth();
        // No redirigir si estamos en rutas públicas (login, register o privacy) y no hemos navegado ya
        const publicPaths = ['/login', '/register'];
        const isPublic =
            publicPaths.includes(location.pathname) ||
            location.pathname.startsWith('/privacy/') ||
            location.pathname === '/delete-data' ||
            location.pathname === '/delete-account';
        if (!isPublic && !hasNavigated.current) {
          hasNavigated.current = true;
          navigate('/login', { replace: true });
        }
      }
    });

    return () => unsubscribe();
  }, [setAuth, clearAuth, navigate, location.pathname]);

  return {
    isAuthenticated,
  };
}

